import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import ShiftHandoffModal from '../../components/Tasks/ShiftHandoffModal';
import OperatorNumpad from './OperatorNumpad';
import { parseApiError } from '../../utils/errorUtils';
import './Operator.css';

/* ─── helpers ─────────────────────────────────────────── */

// Printing measures rolls, meters and kg of good output
const sumRolls = (list) => ({
  rolls: list.length,
  meters: list.reduce((s, r) => s + (parseFloat(r.final_meters) || 0), 0),
  kg: list.reduce((s, r) => s + (parseFloat(r.final_weight_kg) || 0), 0),
});
const formatMeasures = (m, t) => t('operatorPage.measures', { count: m.rolls, meters: m.meters.toFixed(1), kg: m.kg.toFixed(2) });

// Labels live in operator:operatorPage.<group>.<code>; unknown codes fall back to the raw code
const typeLabel = (t, code) => t(`operatorPage.types.${code}`, { defaultValue: code });
// Unassigned tasks an operator can take from Disponibles: new ones, and started ones left without operator
const ACCEPTABLE_STATUSES = ['PENDING', 'READY', 'PAUSED', 'IN_PROGRESS'];
const statusLabel = (t, code) => t(`operatorPage.status.${code}`, { defaultValue: code });

const WASTE_CATEGORIES = ['', 'TRIM', 'SETUP', 'DEFECT', 'SPLICE', 'OTHER'];
const SHIFT_TYPES = ['HD', 'HN', 'HED', 'HEN', 'HEB'];
const ISSUE_TYPES = ['MATERIAL', 'MACHINE', 'QUALITY', 'SAFETY', 'OTHER'];
const SEVERITY_LEVELS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const UOM_OPTIONS = ['PCS', 'ROLL', 'KG', 'M'];

// Backend errors come as { message } (structured) or { detail } (legacy); show whichever arrives
const apiErrorMessage = async (res, fallback) => {
  const body = await res.json().catch(() => null);
  return parseApiError(body).message || fallback;
};

// The backend refuses the same return sent twice (rule material_return_duplicate) and names the one already registered
const returnErrorMessage = async (res, t) => {
  const err = parseApiError(await res.json().catch(() => null));
  if (err.details?.rule === 'material_return_duplicate') {
    return t('operatorPage.errors.returnDuplicate', { number: err.details.return_number });
  }
  return t('operatorPage.errors.returnNotRegistered', { message: err.message || t('operatorPage.errors.returnFailed') });
};

// Max returnable per material: issued minus what earlier returns (not cancelled/rejected) took back (#290).
// Going over it, or returning a material that is not on the requisition, is allowed: the line is flagged,
// needs a reason, and the supervisor (not the warehouse) receives and inspects that return.
const maxReturnable = (mat) => Number(mat.max_returnable ?? Math.max(Number(mat.total_issued || 0) - Number(mat.total_returned || 0), 0));
const fmtQty = (n) => String(Math.round(Number(n || 0) * 1000) / 1000);
const exceedsMax = (value, mat) => !!value && Math.round(parseFloat(value) * 1000) > Math.round(maxReturnable(mat) * 1000);
const hasQty = (it) => !!it.quantity_returned && parseFloat(it.quantity_returned) > 0;
const missingReason = (it) => !(it.flag_reason || '').trim();

/* ─── NumField: tap-to-open numpad ───────────────────────*/
const NumField = ({ label, value, field, setNumpad }) => {
  const { t } = useTranslation('operator');
  return (
  <div className="op-field">
    <div className="op-field-label">{label}</div>
    <div
      className={`op-numfield${field ? ' focused' : ''}`}
      onClick={() => setNumpad(field)}
    >
      {value
        ? <span>{value}</span>
        : <span className="op-numfield-placeholder">{t('operatorPage.placeholders.tapToEnter')}</span>
      }
      <span className="op-numfield-icon">⌨</span>
    </div>
  </div>
  );
};

/* ═══════════════════════════════════════════════════════
   OPERATOR PAGE
   ═══════════════════════════════════════════════════════ */

const OperatorPage = () => {
  const { t } = useTranslation('operator');
  const { user, logout } = useAuth();

  // ─── Main view state ────────────────────────────────
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [filter, setFilter] = useState('mine');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);

  // ─── Workflow progress ───────────────────────────────
  const [workflowProgress, setWorkflowProgress] = useState([]);

  // ─── Roll state (PRINTING) ───────────────────────────
  const [rolls, setRolls] = useState([]);
  const [loadingRolls, setLoadingRolls] = useState(false);
  const [showRollModal, setShowRollModal] = useState(false);
  const [rollForm, setRollForm] = useState({
    roll_barcode: '',
    initial_weight_kg: '',
    final_weight_kg: '',
    final_meters: '',
    waste_category: '',
    waste_notes: '',
  });
  const [savingRoll, setSavingRoll] = useState(false);

  // ─── Session state (non-PRINTING) ───────────────────
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [sessionForm, setSessionForm] = useState({
    quantity_produced: '',
    uom: 'PCS',
    waste_kg: '',
    shift_type: 'HD',
    notes: '',
  });
  const [savingSession, setSavingSession] = useState(false);

  // ─── Complete task ───────────────────────────────────
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completeForm, setCompleteForm] = useState({
    quantity_produced: '',
    uom: 'PCS',
    waste_kg: '',
    shift_type: 'HD',
    notes: '',
  });
  // Production already reported on the task (Registrar Producción, Fin de Turno); see previousTotal
  const [previousLogs, setPreviousLogs] = useState([]);
  const [completing, setCompleting] = useState(false);

  // ─── Shift handoff ───────────────────────────────────
  const [showHandoffModal, setShowHandoffModal] = useState(false);

  // ─── Pause ───────────────────────────────────────────
  const [pausing, setPausing] = useState(false);

  // ─── Issue ───────────────────────────────────────────
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [issueForm, setIssueForm] = useState({ issue_type: 'OTHER', description: '', severity: 'MEDIUM' });
  const [savingIssue, setSavingIssue] = useState(false);

  // ─── Materials ──────────────────────────────────────
  const [showMaterialActionSheet, setShowMaterialActionSheet] = useState(false);
  // Request materials
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [requestItems, setRequestItems] = useState([]); // [{inventory_item_id, requested_quantity, notes}]
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [submittingReq, setSubmittingReq] = useState(false);
  // Return materials
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [issuedMaterials, setIssuedMaterials] = useState([]);
  const [returnItems, setReturnItems] = useState([]); // [{inventory_item_id, requisition_id, quantity_returned, notes, flag_reason}], one per issued material
  const [extraItems, setExtraItems] = useState([]); // materials off the requisition: [{inventory_item_id, item_code, item_name, unit_of_measure, requisition_id, quantity_returned, flag_reason}]
  const [extraSearch, setExtraSearch] = useState(null); // null = closed, else {text, results, loading}
  const [submittingReturn, setSubmittingReturn] = useState(false);
  const submittingReturnRef = useRef(false); // a double tap fires twice before the disabled button re-renders
  const [returnError, setReturnError] = useState('');
  // Req numpad target
  const [matNumpad, setMatNumpad] = useState(null); // {index, field, setter}
  const [matNumpadValue, setMatNumpadValue] = useState('');

  // ─── Accept task ─────────────────────────────────────
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [acceptingTask, setAcceptingTask] = useState(false);
  const [startingTask, setStartingTask] = useState(false);

  // ─── Generic numpad ──────────────────────────────────
  const [numpadField, setNumpadField] = useState(null);
  const [numpadValue, setNumpadValue] = useState('');

  // ─── Error ───────────────────────────────────────────
  const [error, setError] = useState(null);

  /* ─── Load tasks ──────────────────────────────────── */
  const loadTasks = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      let url;
      if (filter === 'mine') {
        url = getApiUrl(`/api/production-tasks/users/${user.user_id}/tasks`);
      } else if (filter === 'available') {
        url = getApiUrl(`/api/production-tasks/tasks?status=${ACCEPTABLE_STATUSES.join(',')}&unassigned=true`);
      } else {
        url = getApiUrl(`/api/production-tasks/users/${user.user_id}/tasks?include_completed=true`);
      }
      const res = await apiFetch(url);
      if (res.ok) {
        const data = await res.json();
        const list = data.tasks || data || [];
        if (filter === 'completed') {
          setTasks(list.filter(task => task.status === 'COMPLETED'));
        } else if (filter === 'available') {
          // Only tasks nobody holds and the operator can take (#328)
          setTasks(list.filter(task => task.assigned_to == null && ACCEPTABLE_STATUSES.includes(task.status)));
        } else {
          setTasks(list.filter(task => task.status !== 'COMPLETED'));
        }
      } else {
        setError(t('operatorPage.errors.loadTasks'));
      }
    } catch (e) {
      console.error('loadTasks:', e);
      setError(t('operatorPage.errors.connection'));
    } finally {
      setLoading(false);
    }
  }, [user, filter, t]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  /* ─── Load rolls ──────────────────────────────────── */
  const loadRolls = useCallback(async (taskId) => {
    setLoadingRolls(true);
    try {
      const res = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/rolls`));
      if (res.ok) {
        const data = await res.json();
        setRolls(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('loadRolls:', e);
    } finally {
      setLoadingRolls(false);
    }
  }, []);

  /* ─── Load workflow progress ──────────────────────── */
  const loadWorkflowProgress = useCallback(async (workflowInstanceId) => {
    if (!workflowInstanceId) { setWorkflowProgress([]); return; }
    try {
      const res = await apiFetch(getApiUrl(`/api/production-tasks/workflow-progress/${workflowInstanceId}`));
      if (res.ok) {
        const data = await res.json();
        setWorkflowProgress(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('loadWorkflowProgress:', e);
    }
  }, []);

  /* ─── Select task ─────────────────────────────────── */
  const handleSelectTask = (task) => {
    setSelectedTask(task);
    setView('detail');
    setWorkflowProgress([]);
    setRolls([]);
    setPreviousLogs([]);
    loadProductionLogs(task.task_id);
    if (task.task_type === 'PRINTING') loadRolls(task.task_id);
    if (task.workflow_instance_id) loadWorkflowProgress(task.workflow_instance_id);
  };

  /* ─── Accept task ─────────────────────────────────── */
  const handleAcceptTask = async () => {
    if (!selectedTask) return;
    setAcceptingTask(true);
    try {
      const res = await apiFetch(getApiUrl('/api/production-tasks/assignments'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_id: selectedTask.task_id,
          assigned_to: user.user_id,
          assigned_by: user.user_id,
        }),
      });
      if (res.ok) {
        await apiFetch(
          getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id}/status?status=IN_PROGRESS`),
          { method: 'PATCH' }
        );
        setSelectedTask(prev => ({ ...prev, status: 'IN_PROGRESS', assigned_to: user.user_id }));
        setShowAcceptModal(false);
        loadTasks();
      } else {
        alert(await apiErrorMessage(res, t('operatorPage.errors.acceptFailed')));
      }
    } catch (e) {
      console.error('acceptTask:', e);
      alert(t('operatorPage.errors.acceptConnection'));
    } finally {
      setAcceptingTask(false);
    }
  };

  /* ─── Start an assigned task ──────────────────────── */
  // ASSIGNED/READY tasks already belong to the operator (planner or supervisor assigned them):
  // starting moves them to IN_PROGRESS, and the backend records actual_start and accepted_at
  const handleStartTask = async () => {
    if (!selectedTask) return;
    setStartingTask(true);
    try {
      const res = await apiFetch(
        getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id}/status?status=IN_PROGRESS`),
        { method: 'PATCH' }
      );
      if (res.ok) {
        setSelectedTask(prev => ({ ...prev, status: 'IN_PROGRESS' }));
        loadTasks();
      } else {
        alert(await apiErrorMessage(res, t('operatorPage.errors.startFailed')));
      }
    } catch (e) {
      console.error('startTask:', e);
      alert(t('operatorPage.errors.startConnection'));
    } finally {
      setStartingTask(false);
    }
  };

  /* ─── Pause / Resume ──────────────────────────────── */
  const handlePauseResume = async () => {
    if (!selectedTask) return;
    const isPaused = selectedTask.status === 'PAUSED';
    const newStatus = isPaused ? 'IN_PROGRESS' : 'PAUSED';
    setPausing(true);
    try {
      const res = await apiFetch(
        getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id}/status?status=${newStatus}`),
        { method: 'PATCH' }
      );
      if (res.ok) {
        setSelectedTask(prev => ({ ...prev, status: newStatus }));
        loadTasks();
      } else {
        alert(t('operatorPage.errors.statusChangeFailed'));
      }
    } catch (e) {
      console.error('pauseResume:', e);
    } finally {
      setPausing(false);
    }
  };

  /* ─── Report issue ────────────────────────────────── */
  const handleSaveIssue = async () => {
    if (!issueForm.description.trim()) { alert(t('operatorPage.errors.describeIssue')); return; }
    setSavingIssue(true);
    try {
      const res = await apiFetch(
        getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id}/report-issue`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(issueForm) }
      );
      if (res.ok) {
        setShowIssueModal(false);
        setIssueForm({ issue_type: 'OTHER', description: '', severity: 'MEDIUM' });
        alert(t('operatorPage.success.issueReported'));
      } else {
        alert(await apiErrorMessage(res, t('operatorPage.errors.reportFailed')));
      }
    } catch (e) {
      console.error('reportIssue:', e);
      alert(t('operatorPage.errors.reportConnection'));
    } finally {
      setSavingIssue(false);
    }
  };

  /* ─── Load available materials (BOM) ─────────────── */
  const handleOpenRequestMaterials = async () => {
    setShowMaterialActionSheet(false);
    if (!selectedTask?.production_order_id) {
      alert(t('operatorPage.errors.noProductionOrder'));
      return;
    }
    setLoadingMaterials(true);
    try {
      const res = await apiFetch(
        getApiUrl(`/api/inventory-requisitions/production-order/${selectedTask.production_order_id}/available-items`)
      );
      if (res.ok) {
        const data = await res.json();
        const mats = Array.isArray(data) ? data : [];
        if (mats.length === 0) {
          alert(t('operatorPage.errors.noBomMaterials'));
          return;
        }
        setAvailableMaterials(mats);
        setRequestItems(mats.map(m => ({ inventory_item_id: m.inventory_item_id, requested_quantity: '', notes: '' })));
        setShowRequestModal(true);
      } else {
        alert(t('operatorPage.errors.bomLoadFailed'));
      }
    } catch (e) {
      console.error('loadAvailableMaterials:', e);
      alert(t('operatorPage.errors.connection'));
    } finally {
      setLoadingMaterials(false);
    }
  };

  /* ─── Submit material requisition ────────────────── */
  const handleSubmitRequisition = async () => {
    // Only the chosen line of each group of alternatives is requested (#322)
    const items = requestItems.filter((i, idx) => availableMaterials[idx]?.selected !== false
      && i.requested_quantity && parseFloat(i.requested_quantity) > 0);
    if (items.length === 0) { alert(t('operatorPage.errors.enterQuantity')); return; }
    setSubmittingReq(true);
    try {
      const payload = {
        production_order_id: selectedTask.production_order_id,
        production_task_id: selectedTask.task_id,
        priority: 'NORMAL',
        notes: null,
        is_over_bom: false,
        over_bom_reason: null,
        items: items.map(i => ({
          inventory_item_id: parseInt(i.inventory_item_id),
          requested_quantity: parseFloat(i.requested_quantity),
          notes: i.notes || null,
        })),
      };
      const res = await apiFetch(
        getApiUrl(`/api/inventory-requisitions/create?requested_by=${user.user_id}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (res.ok) {
        setShowRequestModal(false);
        setRequestItems([]);
        setAvailableMaterials([]);
        alert(t('operatorPage.success.requisitionCreated'));
      } else {
        alert(await apiErrorMessage(res, t('operatorPage.errors.requisitionFailed')));
      }
    } catch (e) {
      console.error('submitRequisition:', e);
      alert(t('operatorPage.errors.connection'));
    } finally {
      setSubmittingReq(false);
    }
  };

  /* ─── Load issued materials (for return) ─────────── */
  const handleOpenReturnMaterials = async () => {
    setShowMaterialActionSheet(false);
    setLoadingMaterials(true);
    try {
      const res = await apiFetch(
        getApiUrl(`/api/inventory-requisitions/task/${selectedTask.task_id}/issued-materials`)
      );
      if (res.ok) {
        const data = await res.json();
        const mats = Array.isArray(data) ? data : [];
        if (mats.length === 0) {
          alert(t('operatorPage.errors.noIssuedMaterials'));
          return;
        }
        setIssuedMaterials(mats);
        setReturnItems(mats.map(m => ({ inventory_item_id: m.inventory_item_id, requisition_id: m.requisition_id, quantity_returned: '', notes: '', flag_reason: '' })));
        setExtraItems([]);
        setExtraSearch(null);
        setReturnError('');
        setShowReturnModal(true);
      } else {
        alert(t('operatorPage.errors.issuedLoadFailed'));
      }
    } catch (e) {
      console.error('loadIssuedMaterials:', e);
      alert(t('operatorPage.errors.connection'));
    } finally {
      setLoadingMaterials(false);
    }
  };

  /* ─── Submit material return ──────────────────────── */
  // The return stays PENDING until the warehouse receives and inspects it
  // (same flow as F1 Inicio); stock only goes back in on restock.
  // One return per requisition, all created in a single backend call: if any line is
  // rejected nothing is registered, so a retry never duplicates what already went through.
  const flaggedReturnLines = () => [
    ...returnItems.filter((it, idx) => exceedsMax(it.quantity_returned, issuedMaterials[idx])),
    ...extraItems.filter(hasQty),
  ];
  const handleSubmitReturn = async () => {
    if (submittingReturnRef.current) return;
    const flagged = flaggedReturnLines();
    if (flagged.some(missingReason)) { setReturnError(t('operatorPage.errors.flagReasonRequired')); return; }
    const items = [...returnItems, ...extraItems].filter(hasQty);
    if (items.length === 0) { setReturnError(t('operatorPage.errors.enterReturnQuantity')); return; }
    const byRequisition = {};
    items.forEach(i => { (byRequisition[i.requisition_id] = byRequisition[i.requisition_id] || []).push(i); });
    const returns = Object.entries(byRequisition).map(([requisitionId, reqItems]) => ({
      requisition_id: parseInt(requisitionId),
      production_task_id: selectedTask.task_id,
      production_order_id: selectedTask.production_order_id,
      return_reason: `Material no usado de la tarea ${selectedTask.task_code}`,
      items: reqItems.map(i => ({
        inventory_item_id: parseInt(i.inventory_item_id),
        quantity_returned: parseFloat(i.quantity_returned),
        notes: i.notes || null,
        flag_reason: flagged.includes(i) ? i.flag_reason.trim() : null,
      })),
    }));
    submittingReturnRef.current = true;
    setSubmittingReturn(true);
    setReturnError('');
    try {
      const res = await apiFetch(
        getApiUrl(`/api/material-returns/create-batch?returned_by=${user.user_id}`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ returns }),
        }
      );
      if (!res.ok) {
        setReturnError(await returnErrorMessage(res, t));
        return;
      }
      const data = await res.json();
      setShowReturnModal(false);
      setReturnItems([]);
      setExtraItems([]);
      setIssuedMaterials([]);
      const numbers = (data.return_numbers || []).join(', ');
      alert(flagged.length
        ? t('operatorPage.success.returnFlagged', { numbers })
        : t('operatorPage.success.returnSent', { numbers }));
    } catch (e) {
      console.error('submitReturn:', e);
      setReturnError(t('operatorPage.errors.returnConnection'));
    } finally {
      submittingReturnRef.current = false;
      setSubmittingReturn(false);
    }
  };

  /* ─── Material off the requisition (#290) ─────────── */
  // e.g. a thinned ink returned under its own code. Materials already on a requisition are listed above.
  const searchExtraMaterial = async (text) => {
    setExtraSearch({ text, results: [], loading: !!text.trim() });
    if (!text.trim()) return;
    try {
      const params = new URLSearchParams({ search: text.trim(), page_size: '20' });
      const res = await apiFetch(getApiUrl(`/api/inventory?${params}`));
      const data = res.ok ? await res.json() : {};
      const listed = new Set([...issuedMaterials, ...extraItems].map(m => Number(m.inventory_item_id)));
      const results = (data.items || []).filter(it => !listed.has(Number(it.item_id)));
      setExtraSearch(prev => (prev && prev.text === text ? { text, results, loading: false } : prev));
    } catch (e) {
      console.error('searchExtraMaterial:', e);
      setExtraSearch(prev => (prev && prev.text === text ? { text, results: [], loading: false } : prev));
    }
  };
  const addExtraMaterial = (it) => {
    setExtraItems(prev => [...prev, {
      inventory_item_id: it.item_id, item_code: it.item_code, item_name: it.item_name, unit_of_measure: it.unit_of_measure,
      requisition_id: issuedMaterials[0]?.requisition_id, quantity_returned: '', flag_reason: '',
    }]);
    setExtraSearch(null);
  };
  const returnRequisitions = [...new Map(issuedMaterials.map(m => [m.requisition_id, m.requisition_number])).entries()];

  /* ─── Numpad helpers ──────────────────────────────── */
  const openNumpad = (field, currentVal, setter) => {
    setNumpadField({ field, setter });
    setNumpadValue(currentVal || '');
  };

  const openRollNumpad = (field) => {
    openNumpad(field, rollForm[field], (v) =>
      setRollForm(prev => ({ ...prev, [field]: v }))
    );
  };

  const openSessionNumpad = (field) => {
    openNumpad(field, sessionForm[field], (v) =>
      setSessionForm(prev => ({ ...prev, [field]: v }))
    );
  };

  const openCompleteNumpad = (field) => {
    openNumpad(field, completeForm[field], (v) =>
      setCompleteForm(prev => ({ ...prev, [field]: v }))
    );
  };

  const confirmNumpad = () => {
    if (numpadField) numpadField.setter(numpadValue);
    setNumpadField(null);
    setNumpadValue('');
  };

  /* ─── Save roll ───────────────────────────────────── */
  const handleSaveRoll = async () => {
    if (!rollForm.initial_weight_kg || !rollForm.final_weight_kg || !rollForm.final_meters) {
      alert(t('operatorPage.errors.rollFieldsRequired'));
      return;
    }
    setSavingRoll(true);
    try {
      const nextSeq = rolls.length + 1;
      const payload = {
        task_id: selectedTask.task_id,
        roll_sequence: nextSeq,
        roll_barcode: rollForm.roll_barcode || null,
        initial_weight_kg: parseFloat(rollForm.initial_weight_kg),
        final_weight_kg: parseFloat(rollForm.final_weight_kg),
        final_meters: parseFloat(rollForm.final_meters),
        waste_category: rollForm.waste_category || null,
        waste_notes: rollForm.waste_notes || null,
        recorded_by: user.user_id,
      };
      const res = await apiFetch(getApiUrl('/api/production-tasks/rolls/consumption'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setShowRollModal(false);
        setRollForm({ roll_barcode: '', initial_weight_kg: '', final_weight_kg: '', final_meters: '', waste_category: '', waste_notes: '' });
        loadRolls(selectedTask.task_id);
      } else {
        alert(await apiErrorMessage(res, t('operatorPage.errors.rollSaveFailed')));
      }
    } catch (e) {
      console.error('saveRoll:', e);
      alert(t('operatorPage.errors.connection'));
    } finally {
      setSavingRoll(false);
    }
  };

  /* ─── Save session ────────────────────────────────── */
  // Adds to the task total; the backend saves the session from the operator's previous report
  // (or from when they took the task) until now.
  const openSessionModal = () => {
    setSessionForm({ quantity_produced: '', uom: previousLogs[0]?.uom || 'PCS', waste_kg: '', shift_type: 'HD', notes: '' });
    setShowSessionModal(true);
  };

  const handleSaveSession = async () => {
    if (isPrinting && pendingRolls.rolls === 0) {
      alert(t('operatorPage.errors.noNewRolls'));
      return;
    }
    if (!isPrinting && !sessionForm.quantity_produced) {
      alert(t('operatorPage.errors.enterProduced'));
      return;
    }
    setSavingSession(true);
    try {
      // In Printing the backend counts the rolls not yet reported (rolls, meters and kg)
      const payload = isPrinting
        ? { shift_type: sessionForm.shift_type, notes: sessionForm.notes || null }
        : {
            quantity_produced: parseFloat(sessionForm.quantity_produced),
            uom: sessionForm.uom,
            waste_kg: sessionForm.waste_kg ? parseFloat(sessionForm.waste_kg) : 0,
            shift_type: sessionForm.shift_type,
            notes: sessionForm.notes || null,
          };
      const res = await apiFetch(
        getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id}/production`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
      );
      if (res.ok) {
        const { total_produced, uom } = await res.json();
        setShowSessionModal(false);
        loadProductionLogs(selectedTask.task_id);
        if (isPrinting) loadRolls(selectedTask.task_id);
        alert(t('operatorPage.success.productionRecorded', { total: total_produced, uom: uom || sessionForm.uom }));
      } else {
        alert(await apiErrorMessage(res, t('operatorPage.errors.sessionSaveFailed')));
      }
    } catch (e) {
      console.error('saveSession:', e);
      alert(t('operatorPage.errors.connection'));
    } finally {
      setSavingSession(false);
    }
  };

  /* ─── Complete task ───────────────────────────────── */
  // The task's quantity is the sum of every report (Registrar Producción, Fin de Turno and this
  // one), each with what was produced since the previous report; when it is the last task of
  // the line, that sum becomes the line's produced quantity.
  const previousTotal = previousLogs.reduce((s, l) => s + (parseFloat(l.quantity_produced) || 0), 0);

  // Printing: each report counts the rolls no report has counted yet; the accumulated rolls, meters
  // and kg are all the task's rolls, each counted once.
  const isPrinting = selectedTask?.task_type === 'PRINTING';
  const rollTotals = sumRolls(rolls);
  const pendingRolls = sumRolls(rolls.filter(r => !r.completion_log_id));

  const loadProductionLogs = async (taskId) => {
    let logs = [];
    try {
      const res = await apiFetch(getApiUrl(`/api/production-tasks/tasks/${taskId}/completion-logs`));
      if (res.ok) {
        const data = await res.json();
        logs = Array.isArray(data) ? data : [];
      }
    } catch (e) {
      console.error('loadCompletionLogs:', e);
    }
    setPreviousLogs(logs);
    return logs;
  };

  const handleOpenComplete = async () => {
    if (isPrinting) loadRolls(selectedTask.task_id);
    const logs = await loadProductionLogs(selectedTask.task_id);
    setCompleteForm({
      quantity_produced: '',
      uom: logs[0]?.uom || (selectedTask.task_type === 'PRINTING' ? 'ROLL' : 'PCS'),
      waste_kg: '',
      shift_type: 'HD',
      notes: '',
    });
    setShowCompleteModal(true);
  };

  const handleCompleteTask = async () => {
    if (isPrinting) {
      if (pendingRolls.rolls === 0 && !(previousTotal > 0)) {
        alert(t('operatorPage.errors.addRollFirst'));
        return;
      }
    } else if (completeForm.quantity_produced === '') {
      alert(t('operatorPage.errors.enterProduced'));
      return;
    }
    // Printing: the backend counts the rolls not yet reported; what is sent here is ignored
    const quantity = isPrinting ? pendingRolls.rolls : parseFloat(completeForm.quantity_produced);
    if (!isPrinting && !(quantity > 0) && !(previousTotal > 0)) {
      alert(t('operatorPage.errors.quantityPositive'));
      return;
    }
    setCompleting(true);
    try {
      const payload = {
        quantity_produced: quantity,
        uom: isPrinting ? 'ROLL' : completeForm.uom,
        completion_notes: completeForm.notes || 'Tarea completada desde tablet.',
        waste_quantity_kg: completeForm.waste_kg ? parseFloat(completeForm.waste_kg) : null,
        shift_type: completeForm.shift_type,
      };
      const res = await apiFetch(
        getApiUrl(`/api/production-tasks/tasks/${selectedTask.task_id}/complete`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
      );
      if (res.ok) {
        setShowCompleteModal(false);
        setView('list');
        setSelectedTask(null);
        loadTasks();
      } else {
        alert(await apiErrorMessage(res, t('operatorPage.errors.completeFailed')));
      }
    } catch (e) {
      console.error('completeTask:', e);
      alert(t('operatorPage.errors.completeConnection'));
    } finally {
      setCompleting(false);
    }
  };

  /* ═══════════════════════════════════════════════════
     RENDER HELPERS
     ═══════════════════════════════════════════════════ */

  // Who owns the task decides what the operator can do: tasks assigned to someone else are read-only
  const assignedTo = selectedTask?.assigned_to ?? null;
  const isMine = assignedTo != null && Number(assignedTo) === Number(user?.user_id);
  const isOthers = assignedTo != null && !isMine;
  const taskStatus = selectedTask?.status;
  // Only the operator holding the task records, pauses or resumes it
  const isActive = taskStatus === 'IN_PROGRESS' && isMine;
  const isPaused = taskStatus === 'PAUSED' && isMine;
  const canRecord = isActive || isPaused;
  // Nobody holds it: accepting assigns it and starts (or resumes, after a shift handoff) the work (#328)
  const canAccept = ACCEPTABLE_STATUSES.includes(taskStatus) && assignedTo == null;
  // Already assigned to this operator but not started (ASSIGNED, or PENDING/READY pre-assigned)
  const canStart = ['ASSIGNED', 'PENDING', 'READY'].includes(taskStatus) && isMine;

  /* ── Task list ──────────────────────────────────────*/
  const renderTaskList = () => (
    <div className="op-content">
      <div className="op-filter-row">
        {[
          { id: 'mine',      label: t('taskFilters.myTasks') },
          { id: 'available', label: t('taskFilters.available') },
          { id: 'completed', label: t('operatorPage.list.completed') },
        ].map(f => (
          <button
            key={f.id}
            className={`op-filter-tab${filter === f.id ? ' active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="op-loading">{t('operatorPage.list.loading')}</div>
      ) : error ? (
        <div className="op-empty">
          <div className="op-empty-icon">⚠</div>
          <div>{error}</div>
          <button className="op-btn-secondary" style={{ marginTop: 16, flex: 'none', padding: '12px 24px' }} onClick={loadTasks}>
            {t('operatorPage.list.retry')}
          </button>
        </div>
      ) : tasks.length === 0 ? (
        <div className="op-empty">
          <div className="op-empty-icon">✓</div>
          <div>{t('operatorPage.list.empty')}</div>
        </div>
      ) : (
        <div className="op-task-grid">
          {tasks.map(task => (
            <div
              key={task.task_id}
              className={`op-task-card card-${task.status}`}
              onClick={() => handleSelectTask(task)}
            >
              <div className="op-card-top">
                <span className={`op-type-badge op-type-${task.task_type}`}>
                  {typeLabel(t, task.task_type)}
                </span>
                <span className={`op-status-badge op-status-${task.status}`}>
                  {statusLabel(t, task.status)}
                </span>
                <span className="op-card-code">{task.task_code}</span>
              </div>
              <div className="op-card-name">{task.task_name}</div>
              <div className="op-card-meta">
                {task.customer_name && (
                  <div className="op-card-meta-row">
                    <span className="op-meta-icon">👤</span> {task.customer_name}
                  </div>
                )}
                {task.product_name && (
                  <div className="op-card-meta-row">
                    <span className="op-meta-icon">📦</span> {task.product_name}
                  </div>
                )}
                {task.po_number && (
                  <div className="op-card-meta-row">
                    <span className="op-meta-icon">🔢</span> {task.po_number}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ── Workflow progress strip ────────────────────────*/
  const renderWorkflowProgress = () => {
    if (!workflowProgress.length) return null;
    const currentIndex = workflowProgress.findIndex(s => s.task_id === selectedTask?.task_id);

    return (
      <div className="op-workflow-strip">
        {workflowProgress.map((step, idx) => {
          const isCurrent = step.task_id === selectedTask?.task_id;
          const isDone = step.status === 'COMPLETED';
          const isPast = idx < currentIndex;
          return (
            <React.Fragment key={step.task_id}>
              <div className={`op-workflow-step${isCurrent ? ' current' : isDone || isPast ? ' done' : ' future'}`}>
                <div className="op-workflow-dot">{isDone || isPast ? '✓' : idx + 1}</div>
                <div className="op-workflow-label">{step.workflow_task_name || step.task_name}</div>
              </div>
              {idx < workflowProgress.length - 1 && (
                <div className={`op-workflow-line${isDone || isPast ? ' done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  /* ── Task detail ────────────────────────────────────*/
  const renderTaskDetail = () => {
    if (!selectedTask) return null;
    const task = selectedTask;
    const totalWaste = rolls.reduce((s, r) => s + (r.waste_weight_kg || 0), 0);

    return (
      <div className="op-detail-wrap">
        {/* Header */}
        <div className="op-detail-header">
          <button className="op-btn-back" onClick={() => { setView('list'); setSelectedTask(null); }}>
            ←
          </button>
          <div className="op-detail-title-block">
            <div className="op-detail-task-code">{task.task_code}</div>
            <div className="op-detail-task-name">{task.task_name}</div>
          </div>
          <span className={`op-status-badge op-status-${task.status}`}>
            {statusLabel(t, task.status)}
          </span>
        </div>

        {/* Workflow progress */}
        {renderWorkflowProgress()}

        {/* Info bar */}
        <div className="op-detail-info-bar">
          {task.customer_name && (
            <div className="op-info-cell">
              <div className="op-info-label">{t('common:ui.customer')}</div>
              <div className="op-info-value">{task.customer_name}</div>
            </div>
          )}
          {task.product_name && (
            <div className="op-info-cell">
              <div className="op-info-label">{t('common:ui.product')}</div>
              <div className="op-info-value">{task.product_name}</div>
            </div>
          )}
          {task.po_number && (
            <div className="op-info-cell">
              <div className="op-info-label">{t('labels.order')}</div>
              <div className="op-info-value">{task.po_number}</div>
            </div>
          )}
          <div className="op-info-cell">
            <div className="op-info-label">{t('common:ui.type')}</div>
            <div className="op-info-value">{typeLabel(t, task.task_type)}</div>
          </div>
        </div>

        {/* Body */}
        <div className="op-detail-body">
          {canAccept && (
            <div className="op-accept-banner">
              {taskStatus === 'PENDING' || taskStatus === 'READY'
                ? t('operatorPage.detail.unassignedBanner')
                : t('operatorPage.detail.unassignedStartedBanner')}
            </div>
          )}

          {canStart && (
            <div className="op-accept-banner">
              {t('operatorPage.detail.assignedBanner')}
            </div>
          )}

          {isOthers && task.status !== 'COMPLETED' && task.status !== 'CANCELLED' && (
            <div className="op-accept-banner" style={{ borderColor: 'rgba(107,114,128,0.4)', background: 'rgba(107,114,128,0.08)' }}>
              {task.assigned_to_name
                ? t('operatorPage.detail.othersBanner', { name: task.assigned_to_name })
                : t('operatorPage.detail.othersBannerNoName')}
            </div>
          )}

          {task.status === 'CANCELLED' && (
            <div className="op-accept-banner" style={{ borderColor: 'rgba(107,114,128,0.4)', background: 'rgba(107,114,128,0.08)' }}>
              {t('operatorPage.detail.cancelledBanner')}
            </div>
          )}

          {isPaused && (
            <div className="op-accept-banner" style={{ borderColor: 'rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)' }}>
              {t('operatorPage.detail.pausedBanner')}
            </div>
          )}

          {/* Rolls (PRINTING) */}
          {task.task_type === 'PRINTING' && (
            <>
              <div className="op-section-title">
                {t('operatorPage.detail.rollsTitle')}
                {rolls.length > 0 && <span className="op-count-badge">{rolls.length}</span>}
              </div>
              {loadingRolls ? (
                <div className="op-loading" style={{ height: 100 }}>{t('operatorPage.detail.loadingRolls')}</div>
              ) : rolls.length === 0 ? (
                <div style={{ color: '#6b7280', fontSize: 15, marginBottom: 16 }}>
                  {t('operatorPage.detail.noRolls')}
                </div>
              ) : (
                <>
                  <div className="op-rolls-grid">
                    {rolls.map(r => (
                      <div key={r.roll_id || r.roll_sequence} className="op-roll-card">
                        <div className="op-roll-num">{t('operatorPage.detail.rollNumber', { number: r.roll_sequence })}</div>
                        <div className="op-roll-weights">
                          {t('operatorPage.detail.rollWeights', { initial: r.initial_weight_kg, final: r.final_weight_kg })}
                        </div>
                        {r.waste_weight_kg != null && (
                          <div className="op-roll-waste">
                            {r.waste_weight_kg < 0
                              ? t('operatorPage.detail.gainedKg', { kg: Math.abs(r.waste_weight_kg).toFixed(3) })
                              : t('operatorPage.detail.wasteKg', { kg: r.waste_weight_kg.toFixed(3) })}
                          </div>
                        )}
                        {r.final_meters && (
                          <div style={{ fontSize: 13, color: '#9da4b8', marginTop: 4 }}>{r.final_meters} m</div>
                        )}
                        {r.roll_barcode && <div className="op-roll-barcode">{r.roll_barcode}</div>}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 15, color: '#9da4b8', marginBottom: 6 }}>
                    {t('operatorPage.detail.accumulated')} <strong style={{ color: '#22c55e' }}>{formatMeasures(rollTotals, t)}</strong>
                  </div>
                  {pendingRolls.rolls > 0 && (
                    <div style={{ fontSize: 14, color: '#9da4b8', marginBottom: 6 }}>
                      {t('operatorPage.detail.notYetRecorded')} <strong style={{ color: '#e8eaf6' }}>{formatMeasures(pendingRolls, t)}</strong>
                    </div>
                  )}
                  {totalWaste !== 0 && (
                    <div style={{ fontSize: 14, color: '#9da4b8', marginBottom: 12 }}>
                      {t('operatorPage.detail.totalWaste')}{' '}
                      <strong style={{ color: '#f59e0b' }}>{Math.abs(totalWaste).toFixed(3)} kg</strong>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {previousLogs.length > 0 && (
            <div style={{ fontSize: 15, color: '#9da4b8', margin: '8px 0 12px' }}>
              {t('operatorPage.detail.producedSoFar')}{' '}
              <strong style={{ color: '#22c55e' }}>{previousTotal} {previousLogs[0]?.uom}</strong>
            </div>
          )}

          {task.task_type !== 'PRINTING' && task.task_type !== 'QUALITY_CHECK' && canRecord && (
            <div className="op-accept-banner" style={{ marginTop: 8 }}>
              {t('operatorPage.detail.hintStandard')}
            </div>
          )}
          {task.task_type === 'PRINTING' && canRecord && (
            <div className="op-accept-banner" style={{ marginTop: 8 }}>
              {t('operatorPage.detail.hintPrinting')}
            </div>
          )}
          {task.task_type === 'QUALITY_CHECK' && canRecord && (
            <div className="op-accept-banner" style={{ marginTop: 8 }}>
              {t('operatorPage.detail.hintQualityCheck')}
            </div>
          )}
        </div>

        {/* Primary action row */}
        {canRecord && (
          <div className="op-action-bar">
            {task.task_type === 'PRINTING' && isActive && (
              <button className="op-btn-primary" onClick={() => setShowRollModal(true)}>
                {t('operatorPage.actions.addRoll')}
              </button>
            )}
            {task.task_type !== 'QUALITY_CHECK' && isActive && (
              <button className="op-btn-primary" onClick={openSessionModal}>
                {t('operatorPage.actions.recordProduction')}
              </button>
            )}

            {/* Pause / Resume */}
            <button
              className={isPaused ? 'op-btn-success' : 'op-btn-secondary'}
              onClick={handlePauseResume}
              disabled={pausing}
            >
              {isPaused ? t('operatorPage.actions.resume') : t('operatorPage.actions.pause')}
            </button>
          </div>
        )}

        {/* Accept task */}
        {canAccept && (
          <div className="op-action-bar">
            <button className="op-btn-primary" onClick={() => setShowAcceptModal(true)}>
              {t('operatorPage.actions.acceptTask')}
            </button>
          </div>
        )}

        {/* Start a task already assigned to this operator */}
        {canStart && (
          <div className="op-action-bar">
            <button className="op-btn-primary" onClick={handleStartTask} disabled={startingTask}>
              {startingTask ? t('operatorPage.actions.starting') : t('operatorPage.actions.startTask')}
            </button>
          </div>
        )}

        {/* Secondary action row */}
        {canRecord && (
          <div className="op-action-bar" style={{ paddingTop: 0 }}>
            <button
              className="op-btn-secondary"
              onClick={() => setShowMaterialActionSheet(true)}
              disabled={loadingMaterials}
            >
              {loadingMaterials ? t('common:ui.loading') : t('operatorPage.actions.materials')}
            </button>
            <button className="op-btn-warning-solid" onClick={() => setShowHandoffModal(true)}>
              {t('operatorPage.actions.endShift')}
            </button>
            <button className="op-btn-secondary" style={{ color: '#ef4444', borderColor: '#ef4444' }} onClick={() => setShowIssueModal(true)}>
              {t('operatorPage.actions.issue')}
            </button>
            <button className="op-btn-success" onClick={handleOpenComplete}>
              {t('operatorPage.actions.complete')}
            </button>
          </div>
        )}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════
     MODALS
     ═══════════════════════════════════════════════════ */

  /* ── Roll modal ─────────────────────────────────────*/
  const renderRollModal = () => {
    if (!showRollModal) return null;
    return (
      <div className="op-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowRollModal(false)}>
        <div className="op-modal">
          <div className="op-modal-title">{t('operatorPage.rollModal.title', { number: rolls.length + 1 })}</div>
          <div className="op-form-row single">
            <div className="op-field">
              <label className="op-field-label">{t('operatorPage.fields.barcode')}</label>
              <input type="text" className="op-field-input" placeholder={t('operatorPage.placeholders.scanOrType')}
                value={rollForm.roll_barcode}
                onChange={e => setRollForm(prev => ({ ...prev, roll_barcode: e.target.value }))}
                style={{ fontSize: 18 }}
              />
            </div>
          </div>
          <div className="op-form-row">
            <NumField label={`${t('operatorPage.fields.initialWeight')} *`} value={rollForm.initial_weight_kg} field="initial"
              setNumpad={() => openRollNumpad('initial_weight_kg')} />
            <NumField label={`${t('operatorPage.fields.finalWeight')} *`} value={rollForm.final_weight_kg} field="final"
              setNumpad={() => openRollNumpad('final_weight_kg')} />
          </div>
          <div className="op-form-row single">
            <NumField label={`${t('operatorPage.fields.finalMeters')} *`} value={rollForm.final_meters} field="meters"
              setNumpad={() => openRollNumpad('final_meters')} />
          </div>
          <div className="op-form-row single">
            <div className="op-field">
              <label className="op-field-label">{t('operatorPage.fields.wasteCategory')}</label>
              <select className="op-field-select" value={rollForm.waste_category}
                onChange={e => setRollForm(prev => ({ ...prev, waste_category: e.target.value }))}>
                {WASTE_CATEGORIES.map(w => <option key={w} value={w}>{t(`operatorPage.wasteCategories.${w || 'none'}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="op-form-row single">
            <div className="op-field">
              <label className="op-field-label">{t('placeholders.notes')}</label>
              <textarea className="op-field-textarea" placeholder={t('operatorPage.placeholders.observations')}
                value={rollForm.waste_notes}
                onChange={e => setRollForm(prev => ({ ...prev, waste_notes: e.target.value }))} />
            </div>
          </div>
          <div className="op-modal-actions">
            <button className="op-btn-secondary" onClick={() => setShowRollModal(false)}>{t('common:ui.cancel')}</button>
            <button className="op-btn-primary" onClick={handleSaveRoll} disabled={savingRoll}>
              {savingRoll ? t('operatorPage.rollModal.saving') : t('operatorPage.rollModal.save')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Session modal ──────────────────────────────────*/
  const renderSessionModal = () => {
    if (!showSessionModal) return null;
    return (
      <div className="op-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowSessionModal(false)}>
        <div className="op-modal">
          <div className="op-modal-title">{t('operatorPage.sessionModal.title')}</div>
          <div className="op-modal-subtitle">{selectedTask?.task_type && typeLabel(t, selectedTask.task_type)}</div>
          {previousLogs.length > 0 && (
            <div style={{ fontSize: 14, color: '#9da4b8', marginBottom: 12 }}>
              {t('operatorPage.sessionModal.alreadyRecorded')}{' '}
              <strong style={{ color: '#e8eaf6' }}>{previousTotal} {previousLogs[0]?.uom}</strong>
            </div>
          )}
          {isPrinting && (
            <div style={{ fontSize: 14, color: '#9da4b8', marginBottom: 12 }}>
              {t('operatorPage.sessionModal.willRecord')}{' '}
              <strong style={{ color: '#e8eaf6' }}>{formatMeasures(pendingRolls, t)}</strong>
            </div>
          )}
          {!isPrinting && (
          <div className="op-form-row">
            <NumField label={`${previousLogs.length > 0 ? t('operatorPage.fields.producedSinceLast') : t('operatorPage.fields.quantityProduced')} *`}
              value={sessionForm.quantity_produced} field="qty"
              setNumpad={() => openSessionNumpad('quantity_produced')} />
            <div className="op-field">
              <label className="op-field-label">UOM</label>
              <select className="op-field-select" value={sessionForm.uom}
                onChange={e => setSessionForm(prev => ({ ...prev, uom: e.target.value }))}>
                {UOM_OPTIONS.map(u => <option key={u} value={u}>{t(`operatorPage.uom.${u}`)}</option>)}
              </select>
            </div>
          </div>
          )}
          {/* Printing: the waste comes from the rolls */}
          <div className={isPrinting ? 'op-form-row single' : 'op-form-row'}>
            {!isPrinting && (
              <NumField label={t('operatorPage.fields.waste')} value={sessionForm.waste_kg} field="waste"
                setNumpad={() => openSessionNumpad('waste_kg')} />
            )}
            <div className="op-field">
              <label className="op-field-label">{t('operatorPage.fields.shiftType')}</label>
              <select className="op-field-select" value={sessionForm.shift_type}
                onChange={e => setSessionForm(prev => ({ ...prev, shift_type: e.target.value }))}>
                {SHIFT_TYPES.map(s => <option key={s} value={s}>{t(`operatorPage.shiftTypes.${s}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="op-form-row single">
            <div className="op-field">
              <label className="op-field-label">{t('placeholders.notes')}</label>
              <textarea className="op-field-textarea" placeholder={t('operatorPage.placeholders.observations')}
                value={sessionForm.notes}
                onChange={e => setSessionForm(prev => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>
          <div className="op-modal-actions">
            <button className="op-btn-secondary" onClick={() => setShowSessionModal(false)}>{t('common:ui.cancel')}</button>
            <button className="op-btn-primary" onClick={handleSaveSession} disabled={savingSession}>
              {savingSession ? t('operatorPage.rollModal.saving') : t('operatorPage.sessionModal.save')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Complete task modal ────────────────────────────*/
  const renderCompleteModal = () => {
    if (!showCompleteModal) return null;
    const quantity = parseFloat(completeForm.quantity_produced) || 0;
    return (
      <div className="op-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowCompleteModal(false)}>
        <div className="op-modal">
          <div className="op-modal-title">{t('dialogs.completeTask.title')}</div>
          <div className="op-modal-subtitle">{selectedTask?.task_name}</div>
          {isPrinting && (
            <div style={{ fontSize: 14, color: '#9da4b8', marginBottom: 12 }}>
              {t('operatorPage.sessionModal.willRecord')}{' '}
              <strong style={{ color: '#e8eaf6' }}>{formatMeasures(pendingRolls, t)}</strong>
              <br />
              {t('operatorPage.completeModal.taskAccumulated')} <strong style={{ color: '#22c55e' }}>{formatMeasures(rollTotals, t)}</strong>
            </div>
          )}
          {previousLogs.length > 0 && (
            <div style={{ fontSize: 14, color: '#9da4b8', marginBottom: 12 }}>
              {t('operatorPage.sessionModal.alreadyRecorded')}{' '}
              <strong style={{ color: '#e8eaf6' }}>{previousTotal} {previousLogs[0]?.uom}</strong>
              {!isPrinting && (
                <>
                  {' '}{t('operatorPage.completeModal.totalOnComplete')}{' '}
                  <strong style={{ color: '#22c55e' }}>{previousTotal + quantity} {completeForm.uom}</strong>
                </>
              )}
            </div>
          )}
          {!isPrinting && (
          <div className="op-form-row">
            <NumField
              label={`${previousLogs.length > 0 ? t('operatorPage.fields.producedSinceLast') : t('operatorPage.fields.quantityProduced')} *`}
              value={completeForm.quantity_produced} field="qty"
              setNumpad={() => openCompleteNumpad('quantity_produced')} />
            <div className="op-field">
              <label className="op-field-label">UOM</label>
              <select className="op-field-select" value={completeForm.uom}
                onChange={e => setCompleteForm(prev => ({ ...prev, uom: e.target.value }))}>
                {UOM_OPTIONS.map(u => <option key={u} value={u}>{t(`operatorPage.uom.${u}`)}</option>)}
              </select>
            </div>
          </div>
          )}
          <div className="op-form-row">
            <NumField label={t('operatorPage.fields.waste')} value={completeForm.waste_kg} field="waste"
              setNumpad={() => openCompleteNumpad('waste_kg')} />
            <div className="op-field">
              <label className="op-field-label">{t('operatorPage.fields.shiftType')}</label>
              <select className="op-field-select" value={completeForm.shift_type}
                onChange={e => setCompleteForm(prev => ({ ...prev, shift_type: e.target.value }))}>
                {SHIFT_TYPES.map(s => <option key={s} value={s}>{t(`operatorPage.shiftTypes.${s}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="op-form-row single">
            <div className="op-field">
              <label className="op-field-label">{t('placeholders.notes')}</label>
              <textarea className="op-field-textarea" placeholder={t('operatorPage.placeholders.observations')}
                value={completeForm.notes}
                onChange={e => setCompleteForm(prev => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>
          <div className="op-modal-actions">
            <button className="op-btn-secondary" onClick={() => setShowCompleteModal(false)}>{t('common:ui.cancel')}</button>
            <button className="op-btn-success" onClick={handleCompleteTask} disabled={completing}>
              {completing ? t('operatorPage.completeModal.completing') : t('operatorPage.completeModal.submit')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Issue modal ────────────────────────────────────*/
  const renderIssueModal = () => {
    if (!showIssueModal) return null;
    return (
      <div className="op-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowIssueModal(false)}>
        <div className="op-modal">
          <div className="op-modal-title">{t('operatorPage.issueModal.title')}</div>
          <div className="op-form-row">
            <div className="op-field">
              <label className="op-field-label">{t('operatorPage.issueModal.issueType')}</label>
              <select className="op-field-select" value={issueForm.issue_type}
                onChange={e => setIssueForm(prev => ({ ...prev, issue_type: e.target.value }))}>
                {ISSUE_TYPES.map(it => <option key={it} value={it}>{t(`operatorPage.issueTypes.${it}`)}</option>)}
              </select>
            </div>
            <div className="op-field">
              <label className="op-field-label">{t('operatorPage.issueModal.severity')}</label>
              <select className="op-field-select" value={issueForm.severity}
                onChange={e => setIssueForm(prev => ({ ...prev, severity: e.target.value }))}>
                {SEVERITY_LEVELS.map(s => <option key={s} value={s}>{t(`operatorPage.severity.${s}`)}</option>)}
              </select>
            </div>
          </div>
          <div className="op-form-row single">
            <div className="op-field">
              <label className="op-field-label">{t('common:ui.description')} *</label>
              <textarea className="op-field-textarea" rows={4}
                placeholder={t('operatorPage.placeholders.describeIssue')}
                value={issueForm.description}
                onChange={e => setIssueForm(prev => ({ ...prev, description: e.target.value }))} />
            </div>
          </div>
          <div className="op-modal-actions">
            <button className="op-btn-secondary" onClick={() => setShowIssueModal(false)}>{t('common:ui.cancel')}</button>
            <button className="op-btn-danger" onClick={handleSaveIssue} disabled={savingIssue}
              style={{ flex: 1, padding: 16, border: 'none', borderRadius: 10, fontSize: 17, fontWeight: 700, cursor: 'pointer', minHeight: 56 }}>
              {savingIssue ? t('operatorPage.issueModal.sending') : t('operatorPage.issueModal.submit')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Material action sheet ──────────────────────────*/
  const renderMaterialActionSheet = () => {
    if (!showMaterialActionSheet) return null;
    return (
      <div className="op-numpad-overlay" onClick={(e) => e.target === e.currentTarget && setShowMaterialActionSheet(false)}>
        <div className="op-numpad-sheet">
          <div className="op-numpad-field-label" style={{ fontSize: 16, marginBottom: 16 }}>
            {t('operatorPage.materialSheet.title', { task: selectedTask?.task_name })}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button className="op-btn-primary" style={{ flex: 'none' }} onClick={handleOpenRequestMaterials}>
              {t('operatorPage.materialSheet.request')}
            </button>
            <button className="op-btn-secondary" style={{ flex: 'none' }} onClick={handleOpenReturnMaterials}>
              {t('operatorPage.materialSheet.return')}
            </button>
            <button className="op-btn-secondary" style={{ flex: 'none', borderColor: '#35355a', color: '#9da4b8' }}
              onClick={() => setShowMaterialActionSheet(false)}>
              {t('common:ui.cancel')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Request materials modal ────────────────────────*/
  // Alternatives (#322): the BOM lists substrates that replace each other; one per group is asked for
  // (the main one or the one with stock, chosen by the backend) and the operator can switch to another.
  const switchAlternative = (group, itemId) => {
    setAvailableMaterials(prev => prev.map(m => (m.alternative_group === group
      ? { ...m, selected: m.inventory_item_id === itemId } : m)));
    setRequestItems(prev => prev.map((it, i) => (availableMaterials[i]?.alternative_group === group
      ? { ...it, requested_quantity: '' } : it)));
  };

  const renderAlternativePicker = (mat) => {
    const members = availableMaterials.filter(m => mat.alternative_group && m.alternative_group === mat.alternative_group);
    if (members.length < 2) return null;
    return (
      <div style={{ marginBottom: 8 }}>
        <label className="op-field-label">{t('operatorPage.requestModal.alternatives', { count: members.length - 1 })}</label>
        <select className="op-field-select" value={mat.inventory_item_id}
          aria-label={t('operatorPage.requestModal.alternatives', { count: members.length - 1 })}
          onChange={e => switchAlternative(mat.alternative_group, parseInt(e.target.value, 10))}>
          {members.map(m => (
            <option key={m.inventory_item_id} value={m.inventory_item_id}>
              {t('operatorPage.requestModal.alternativeOption', { name: m.item_name, code: m.item_code, available: m.total_available_quantity, uom: m.unit_of_measure })}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderRequestModal = () => {
    if (!showRequestModal) return null;
    return (
      <div className="op-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowRequestModal(false)}>
        <div className="op-modal">
          <div className="op-modal-title">{t('operatorPage.requestModal.title')}</div>
          <div className="op-modal-subtitle">{t('operatorPage.requestModal.subtitle')}</div>
          {availableMaterials.map((mat, idx) => mat.selected === false ? null : (
            <div key={`${mat.requisition_id}-${mat.inventory_item_id}`} style={{ marginBottom: 14, padding: '12px 14px', background: '#0f0f14', borderRadius: 8, border: '1px solid #25253d' }}>
              {renderAlternativePicker(mat)}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#e8eaf6' }}>{mat.item_name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>
                    {t('operatorPage.requestModal.bomLine', { code: mat.item_code, perUnit: mat.quantity_per_unit, uom: mat.unit_of_measure, orderQty: mat.order_quantity, max: mat.max_quantity })}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: '#22c55e', textAlign: 'right' }}>
                  {t('operatorPage.requestModal.available')}<br />{mat.total_available_quantity} {mat.unit_of_measure}
                </div>
              </div>
              <div
                className="op-numfield"
                onClick={() => {
                  setMatNumpad({ index: idx, field: 'requested_quantity', maxVal: mat.max_quantity, uom: mat.unit_of_measure });
                  setMatNumpadValue(requestItems[idx]?.requested_quantity || '');
                }}
              >
                {requestItems[idx]?.requested_quantity
                  ? <span>{requestItems[idx].requested_quantity} {mat.unit_of_measure}</span>
                  : <span className="op-numfield-placeholder">{t('operatorPage.placeholders.tapForQuantity')}</span>
                }
                <span className="op-numfield-icon">⌨</span>
              </div>
            </div>
          ))}
          <div className="op-modal-actions">
            <button className="op-btn-secondary" onClick={() => setShowRequestModal(false)}>{t('common:ui.cancel')}</button>
            <button className="op-btn-primary" onClick={handleSubmitRequisition} disabled={submittingReq}>
              {submittingReq ? t('operatorPage.issueModal.sending') : t('operatorPage.requestModal.submit')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Return materials modal ─────────────────────────*/
  // A flagged line (over the max or off the requisition) is allowed: it shows a warning and asks for a reason (#290)
  const renderFlagReason = (it, onChange, warning) => (
    <div style={{ marginTop: 8 }}>
      <div role="alert" style={{ fontSize: 13, color: '#fcd34d', marginBottom: 6 }}>{warning}</div>
      <label className="op-field-label">{t('operatorPage.fields.reason')} *</label>
      <textarea className="op-field-textarea" rows={2} aria-label={t('operatorPage.fields.reason')}
        placeholder={t('operatorPage.placeholders.returnReason')}
        value={it.flag_reason || ''} onChange={e => onChange(e.target.value)}
        style={{ borderColor: missingReason(it) ? '#ef4444' : undefined }} />
    </div>
  );

  const renderReturnModal = () => {
    if (!showReturnModal) return null;
    const flagged = flaggedReturnLines();
    const reasonsMissing = flagged.some(missingReason);
    const setReturnField = (idx, field, value) => setReturnItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
    const setExtraField = (idx, field, value) => setExtraItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
    return (
      <div className="op-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowReturnModal(false)}>
        <div className="op-modal">
          <div className="op-modal-title">{t('operatorPage.returnModal.title')}</div>
          <div className="op-modal-subtitle">{t('operatorPage.returnModal.subtitle')}</div>
          {issuedMaterials.map((mat, idx) => {
            const max = maxReturnable(mat);
            const item = returnItems[idx] || {};
            const value = item.quantity_returned;
            const over = exceedsMax(value, mat);
            return (
            <div key={`${mat.requisition_id}-${mat.inventory_item_id}`} style={{ marginBottom: 14, padding: '12px 14px', background: '#0f0f14', borderRadius: 8, border: `1px solid ${over ? '#f59e0b' : '#25253d'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#e8eaf6' }}>{mat.item_name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{mat.item_code}{mat.requisition_number ? ` · ${mat.requisition_number}` : ''}</div>
                </div>
                <div style={{ fontSize: 13, color: '#9ca3af', textAlign: 'right', lineHeight: 1.5 }}>
                  <div>{t('operatorPage.returnModal.issued')} <span style={{ color: '#f59e0b' }}>{fmtQty(mat.total_issued)} {mat.unit_of_measure}</span></div>
                  <div>{t('operatorPage.returnModal.alreadyReturned')} <span style={{ color: '#e8eaf6' }}>{fmtQty(mat.total_returned)} {mat.unit_of_measure}</span></div>
                  <div>{t('operatorPage.returnModal.maxReturn')} <strong style={{ color: '#22c55e' }}>{fmtQty(max)} {mat.unit_of_measure}</strong></div>
                </div>
              </div>
              {max <= 0 && (
                <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>{t('operatorPage.returnModal.allReturned')}</div>
              )}
              <div
                className="op-numfield"
                onClick={() => {
                  setMatNumpad({ index: idx, field: 'quantity_returned', maxVal: fmtQty(max), uom: mat.unit_of_measure, isReturn: true });
                  setMatNumpadValue(value || '');
                }}
              >
                {value
                  ? <span>{value} {mat.unit_of_measure}</span>
                  : <span className="op-numfield-placeholder">{t('operatorPage.placeholders.tapForQuantity')}</span>
                }
                <span className="op-numfield-icon">⌨</span>
              </div>
              {over && renderFlagReason(item, v => setReturnField(idx, 'flag_reason', v),
                t('operatorPage.returnModal.overWarning', { max: fmtQty(max), uom: mat.unit_of_measure, over: fmtQty(parseFloat(value) - max) }))}
            </div>
            );
          })}

          {extraItems.map((it, idx) => (
            <div key={`extra-${it.inventory_item_id}`} style={{ marginBottom: 14, padding: '12px 14px', background: '#0f0f14', borderRadius: 8, border: '1px solid #f59e0b' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#e8eaf6' }}>{it.item_name}</div>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{t('operatorPage.returnModal.offRequisitionCode', { code: it.item_code })}</div>
                </div>
                <button className="op-btn-secondary" style={{ flex: 'none', minHeight: 40, padding: '6px 12px' }}
                  onClick={() => setExtraItems(prev => prev.filter((_, i) => i !== idx))}>{t('common:ui.remove')}</button>
              </div>
              {returnRequisitions.length > 1 && (
                <select className="op-field-select" aria-label={t('operatorPage.returnModal.requisition')} value={it.requisition_id}
                  onChange={e => setExtraField(idx, 'requisition_id', Number(e.target.value))} style={{ marginBottom: 8 }}>
                  {returnRequisitions.map(([id, number]) => <option key={id} value={id}>{number || id}</option>)}
                </select>
              )}
              <div
                className="op-numfield"
                onClick={() => {
                  setMatNumpad({ index: idx, field: 'quantity_returned', uom: it.unit_of_measure, isReturn: true, extra: true });
                  setMatNumpadValue(it.quantity_returned || '');
                }}
              >
                {it.quantity_returned
                  ? <span>{it.quantity_returned} {it.unit_of_measure}</span>
                  : <span className="op-numfield-placeholder">{t('operatorPage.placeholders.tapForQuantity')}</span>
                }
                <span className="op-numfield-icon">⌨</span>
              </div>
              {hasQty(it) && renderFlagReason(it, v => setExtraField(idx, 'flag_reason', v),
                t('operatorPage.returnModal.offRequisitionWarning'))}
            </div>
          ))}

          {extraSearch ? (
            <div style={{ marginBottom: 14 }}>
              <input type="text" className="op-field-input" autoFocus aria-label={t('operatorPage.returnModal.searchMaterial')}
                placeholder={t('operatorPage.placeholders.materialSearch')} value={extraSearch.text}
                onChange={e => searchExtraMaterial(e.target.value)} />
              {extraSearch.loading && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{t('operatorPage.returnModal.searching')}</div>}
              {extraSearch.results.map(r => (
                <button key={r.item_id} className="op-btn-secondary" style={{ display: 'block', width: '100%', textAlign: 'left', marginTop: 6 }}
                  onClick={() => addExtraMaterial(r)}>
                  {r.item_code} · {r.item_name}
                </button>
              ))}
              {!extraSearch.loading && extraSearch.text.trim() && extraSearch.results.length === 0 && (
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>{t('operatorPage.returnModal.noResults')}</div>
              )}
              <button className="op-btn-secondary" style={{ marginTop: 6 }} onClick={() => setExtraSearch(null)}>{t('operatorPage.returnModal.closeSearch')}</button>
            </div>
          ) : (
            <button className="op-btn-secondary" style={{ width: '100%', marginBottom: 14 }}
              onClick={() => setExtraSearch({ text: '', results: [], loading: false })}>
              {t('operatorPage.returnModal.addOffRequisition')}
            </button>
          )}

          {flagged.length > 0 && (
            <div style={{ marginBottom: 12, fontSize: 13, color: '#fcd34d' }}>
              {t('operatorPage.returnModal.flaggedLines', { count: flagged.length })}
              {reasonsMissing ? ` ${t('operatorPage.errors.flagReasonRequired')}` : ''}
            </div>
          )}
          {returnError && (
            <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: '#3b1111', color: '#fca5a5', fontSize: 14 }}>
              {returnError}
            </div>
          )}
          <div className="op-modal-actions">
            <button className="op-btn-secondary" onClick={() => setShowReturnModal(false)}>{t('common:ui.cancel')}</button>
            <button
              className="op-btn-primary"
              onClick={handleSubmitReturn}
              disabled={submittingReturn || reasonsMissing}
            >
              {submittingReturn ? t('operatorPage.returnModal.sending') : t('operatorPage.returnModal.confirm')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Materials numpad ───────────────────────────────*/
  const renderMatNumpad = () => {
    if (!matNumpad) return null;
    const isReturn = matNumpad.isReturn;
    return (
      <OperatorNumpad
        label={isReturn
          ? (matNumpad.extra
            ? t('operatorPage.fields.returnQty', { uom: matNumpad.uom })
            : t('operatorPage.fields.returnQtyMax', { max: matNumpad.maxVal, uom: matNumpad.uom }))
          : t('operatorPage.fields.requestQtyMax', { max: matNumpad.maxVal, uom: matNumpad.uom })}
        value={matNumpadValue}
        onChange={setMatNumpadValue}
        onConfirm={() => {
          const { index, field } = matNumpad;
          if (isReturn) {
            (matNumpad.extra ? setExtraItems : setReturnItems)(prev => prev.map((it, i) => i === index ? { ...it, [field]: matNumpadValue } : it));
          } else {
            setRequestItems(prev => prev.map((it, i) => i === index ? { ...it, [field]: matNumpadValue } : it));
          }
          setMatNumpad(null);
          setMatNumpadValue('');
        }}
        onCancel={() => { setMatNumpad(null); setMatNumpadValue(''); }}
      />
    );
  };

  /* ── Accept task modal ──────────────────────────────*/
  const renderAcceptModal = () => {
    if (!showAcceptModal || !selectedTask) return null;
    return (
      <div className="op-modal-overlay">
        <div className="op-modal">
          <div className="op-modal-title">{t('operatorPage.actions.acceptTask')}</div>
          <p style={{ color: '#9da4b8', marginBottom: 20, fontSize: 16 }}>
            <Trans t={t} i18nKey="operatorPage.acceptModal.question" values={{ name: selectedTask.task_name }}
              components={{ strong: <strong style={{ color: '#e8eaf6' }} /> }} />
          </p>
          <div className="op-modal-actions">
            <button className="op-btn-secondary" onClick={() => setShowAcceptModal(false)}>{t('common:ui.cancel')}</button>
            <button className="op-btn-primary" onClick={handleAcceptTask} disabled={acceptingTask}>
              {acceptingTask ? t('operatorPage.acceptModal.accepting') : t('operatorPage.acceptModal.confirm')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ── Generic numpad overlay ─────────────────────────*/
  const renderNumpad = () => {
    if (!numpadField) return null;
    const labels = {
      initial_weight_kg: t('operatorPage.fields.initialWeight'),
      final_weight_kg: t('operatorPage.fields.finalWeight'),
      final_meters: t('operatorPage.fields.finalMeters'),
      quantity_produced: t('operatorPage.fields.quantityProduced'),
      waste_kg: t('operatorPage.fields.waste'),
    };
    return (
      <OperatorNumpad
        label={labels[numpadField.field] || numpadField.field}
        value={numpadValue}
        onChange={setNumpadValue}
        onConfirm={confirmNumpad}
        onCancel={() => setNumpadField(null)}
      />
    );
  };

  /* ─── Main render ─────────────────────────────────── */
  return (
    <div className="operator-app">
      <div className="op-topbar">
        <div className="op-topbar-brand">MPS</div>
        <div className="op-topbar-user">
          <Trans t={t} i18nKey="operatorPage.topbar.greeting"
            values={{ name: user?.full_name || user?.username || t('operatorPage.topbar.operatorFallback') }}
            components={{ strong: <strong /> }} />
        </div>
        <button className="op-btn-logout" onClick={logout}>{t('operatorPage.topbar.logout')}</button>
      </div>

      {view === 'list' ? renderTaskList() : renderTaskDetail()}

      {renderRollModal()}
      {renderSessionModal()}
      {renderCompleteModal()}
      {renderIssueModal()}
      {renderMaterialActionSheet()}
      {renderRequestModal()}
      {renderReturnModal()}
      {renderMatNumpad()}
      {renderAcceptModal()}
      {renderNumpad()}

      {showHandoffModal && selectedTask && (
        <ShiftHandoffModal
          isOpen={showHandoffModal}
          onClose={() => setShowHandoffModal(false)}
          taskId={selectedTask.task_id}
          taskType={selectedTask.task_type}
          onHandoffComplete={() => { setShowHandoffModal(false); loadTasks(); }}
          taskContext={{ task: selectedTask }}
        />
      )}
    </div>
  );
};

export default OperatorPage;
